import React from "react";
import { Smile, Moon, Leaf, Gift, Users, CheckCircle, Briefcase, BarChart3, Handshake, Mail, TrendingUp, Shield, Heart, Smartphone, GraduationCap, UserPlus } from "lucide-react";
import Header from "../components/Header";

export default function EntreprisePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-violet-50">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-accent-200/30 to-violet-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-primary-200/30 to-secondary-200/30 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-secondary-100 px-4 py-2 rounded-full mb-6 border border-primary-200">
                <Briefcase className="w-4 h-4 text-secondary-600" />
                <span className="text-sm font-medium text-primary-700">Solution Entreprise</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-primary-800 via-secondary-700 to-primary-800 bg-clip-text text-transparent">
                  Offrez du bien-être à vos employés.
                </span>
                <br />
                <span className="bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent">
                  Facilement. Durablement.
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 max-w-2xl leading-relaxed">
                Stress, fatigue, perte de motivation ? Vos équipes font face à de nouveaux défis. Avec WeZen, aidez-les à retrouver équilibre, énergie et sérénité au quotidien.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="mailto:contact@wezen.app"
                  className="group flex items-center justify-center gap-2 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-8 py-4 rounded-full hover:from-secondary-600 hover:to-secondary-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Demander un devis
                </a>
              </div>
            </div>
            
            <div className="flex-1 relative">
              <div className="relative z-10 transform hover:scale-105 transition duration-500">
                <img
                  src="femme-entreprise-wezen.jpeg"
                  alt="Bien-être en entreprise"
                  className="w-full max-w-lg mx-auto rounded-[2.5rem] shadow-2xl object-cover"
                />
            
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -left-4 bg-gradient-to-br from-accent-400 to-accent-500 p-3 rounded-2xl shadow-lg animate-float">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-violet-400 to-violet-500 p-3 rounded-2xl shadow-lg animate-float" style={{ animationDelay: '1s' }}>
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-accent-50/30 to-violet-50/30"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">
              Une appli bien-être conçue pour les entreprises
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              WeZen est une application simple et bienveillante qui accompagne vos salariés dans la gestion du stress, du sommeil, de la motivation et de la charge mentale.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-accent-100/50 hover:border-accent-300 transform hover:-translate-y-2">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Smile className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-primary-800 mb-2">Méditations guidées</h3>
              <p className="text-gray-600 text-sm">Exercices de respiration et méditations adaptées</p>
            </div>
            
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-violet-100/50 hover:border-violet-300 transform hover:-translate-y-2">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Moon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-primary-800 mb-2">Pauses bien-être</h3>
              <p className="text-gray-600 text-sm">Contenus audio courts et apaisants</p>
            </div>
            
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-accent-100/50 hover:border-accent-300 transform hover:-translate-y-2">
              <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-primary-800 mb-2">Routines personnalisées</h3>
              <p className="text-gray-600 text-sm">Adaptées aux environnements de travail</p>
            </div>
            
            <div className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-violet-100/50 hover:border-violet-300 transform hover:-translate-y-2">
              <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-primary-800 mb-2">Conseils holistiques</h3>
              <p className="text-gray-600 text-sm">Nutrition, sommeil, émotions et motivation</p>
            </div>
          </div>
          
          {/* Why teams will love it */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-gray-200/50 shadow-lg">
            <h3 className="text-2xl font-bold text-primary-800 mb-8 text-center">Pourquoi vos équipes vont aimer</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-accent-700 w-6 h-6 flex-shrink-0" />
                <span className="text-gray-800 font-medium">Accessible partout, tout le temps</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-violet-700 w-6 h-6 flex-shrink-0" />
                <span className="text-gray-800 font-medium">Sans pression ni jugement</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-accent-700 w-6 h-6 flex-shrink-0" />
                <span className="text-gray-800 font-medium">Contenus courts et concrets</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-violet-700 w-6 h-6 flex-shrink-0" />
                <span className="text-gray-800 font-medium">Soutien de l'entreprise</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E-Learning Platform Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">
              Une plateforme E-learning
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - For teams */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-primary-800 mb-6">Pour les équipes :</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    Des contenus <span className="font-semibold text-accent-600">accessibles à tous</span>, pour :
                  </p>
                  <ul className="space-y-3 ml-4">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-accent-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">apprendre à <span className="font-semibold">gérer son stress</span>,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-accent-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">booster la <span className="font-semibold">confiance en soi</span> et la motivation,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-accent-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">construire une <span className="font-semibold">routine bien-être</span> quotidienne,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-accent-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">améliorer sa <span className="font-semibold">gestion des émotions</span></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-accent-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">découvrir comment tirer <span className="font-semibold">pleinement profit</span> de l'application WeZen et de ses fonctionnalités.</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Illustration for teams */}
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center mb-4 mx-auto">
                    <Users className="w-12 h-12 text-blue-600" />
                  </div>
                  <p className="text-blue-800 font-medium">Contenus accessibles à tous</p>
                </div>
              </div>
            </div>
            
            {/* Right side - For managers */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-primary-800 mb-6">Pour les managers :</h3>
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">
                    Nous leur donnons les clés d'un <span className="font-semibold text-violet-600">management bienveillant et humain</span>, basé sur :
                  </p>
                  <ul className="space-y-3 ml-4">
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">la <span className="font-semibold">prévention</span> des risques psychosociaux,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">l'amélioration de la <span className="font-semibold">qualité de vie au travail</span>,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">une <span className="font-semibold">communication</span> plus fluide,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">le renforcement de la <span className="font-semibold">cohésion d'équipe</span>,</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">une meilleure <span className="font-semibold">motivation</span> collective et un véritable <span className="font-semibold">engagement</span> au quotidien.</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Illustration for managers */}
              <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center mb-4 mx-auto">
                    <GraduationCap className="w-12 h-12 text-violet-600" />
                  </div>
                  <p className="text-violet-800 font-medium">Management bienveillant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Masterclass Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-violet-50/50 via-white to-purple-50/50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">
              Masterclass en présentiel
            </h2>
            <h3 className="text-xl md:text-2xl font-semibold text-violet-700 mb-8">
              Une formation bien-être dans vos locaux
            </h3>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <p className="text-gray-700 leading-relaxed text-lg">
                  Nous nous déplaçons dans vos services, magasins ou antennes* 
                  pour proposer à vos équipes un moment concret, humain et utile :
                </p>
                
                <ul className="space-y-4 ml-6">
                  <li className="flex items-start gap-4">
                    <div className="w-3 h-3 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700 text-lg">Techniques simples et efficaces pour mieux gérer le stress,</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-3 h-3 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700 text-lg">Exercices pour booster l'énergie, améliorer la concentration,</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-3 h-3 bg-violet-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-gray-700 text-lg">Conseils pour installer des nouvelles habitudes durables dans leur quotidien professionnel et personnel.</span>
                  </li>
                </ul>
                
                <div className="bg-violet-50 rounded-2xl p-6 border-l-4 border-violet-500">
                  <h4 className="font-bold text-violet-800 mb-3 text-lg">Objectif :</h4>
                  <p className="text-violet-700 leading-relaxed">
                    Offrir à vos équipes des outils concrets, accessibles et immédiatement applicables pour un bien-être durable.
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 italic leading-relaxed">
                    *La masterclass en présentiel est proposée sous conditions (engagement annuel, taille de l'entreprise, faisabilité logistique). Elle sera étudiée au cas par cas selon vos besoins et votre structure.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right side - Illustration */}
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-violet-100 to-purple-100 rounded-3xl p-12 shadow-lg">
                <div className="text-center space-y-6">
                  {/* Main illustration */}
                  <div className="bg-white rounded-2xl p-6 shadow-md">
                    <img 
                      src="/masterclass-wezen.png" 
                      alt="Formation bien-être en présentiel - Masterclass WeZen"
                      className="w-full h-auto rounded-lg"
                    />
                    <div className="mt-4 text-center">
                      <p className="text-violet-800 font-semibold text-lg">Formation interactive</p>
                      <p className="text-violet-600 text-sm">Dans vos locaux</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mb-2 mx-auto">
                        <Smartphone className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">Outils pratiques</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center mb-2 mx-auto">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm font-medium text-gray-700">Résultats durables</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-accent-50/30 via-white to-violet-50/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-4">
              Ce que vous y gagnez, concrètement
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary-800 mb-1">Moins d'absentéisme</h4>
                  <p className="text-gray-600 text-sm">Réduction de la fatigue chronique et amélioration de la santé mentale</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary-800 mb-1">Meilleure productivité</h4>
                  <p className="text-gray-600 text-sm">Concentration accrue et performance optimisée</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary-800 mb-1">Plus de cohésion</h4>
                  <p className="text-gray-600 text-sm">Engagement renforcé et esprit d'équipe</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-primary-800 mb-1">Valorisation RH</h4>
                  <p className="text-gray-600 text-sm">Politique QVT reconnue et attractive</p>
                </div>
              </div>
            </div>
            
            {/* Pricing Card */}
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-accent-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-primary-800 mb-2">Une offre simple et flexible</h3>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-4xl font-bold text-accent-600">3,33 € HT</span>
                  <span className="text-gray-600">/ mois / employé</span>
                </div>
                <p className="text-sm text-gray-500">Tarif réduit entreprise</p>
              </div>
              
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <Smartphone className="text-accent-700 w-5 h-5" />
                  <span className="text-gray-800 font-medium">Accès à l'ensemble de l'application</span>
                </div>
                <div className="flex items-center gap-3">
                  <GraduationCap className="text-violet-700 w-5 h-5" />
                  <span className="text-gray-800 font-medium">Plateforme E-Learning</span>
                </div>
                <div className="flex items-center gap-3">
                  <UserPlus className="text-accent-700 w-5 h-5" />
                  <span className="text-gray-800 font-medium">Masterclass en présentiel</span>
                </div>
              </div>
              
              <a
                href="mailto:contact@wezen.app"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-2 font-semibold"
              >
                <Mail className="w-5 h-5" />
                Demander un devis
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-accent-50 to-violet-50"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-accent-200 to-violet-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-violet-200 to-accent-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-800 via-accent-700 to-violet-700 bg-clip-text text-transparent">
                Découvrez WeZen en action
              </span>
            </h2>
            <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto">
              Demandez un devis sur-mesure ou testez gratuitement WeZen
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="mailto:contact@wezen.app"
                className="group flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-semibold"
              >
                <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                contact@wezen.app
              </a>
            </div>
            
            <p className="text-sm text-gray-600">
              Offrir WeZen, c'est offrir un souffle de mieux-être à ceux qui font vivre votre entreprise
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100">
        <div className="container mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Logo and description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-gradient-to-br from-secondary-400 to-primary-500 rounded-lg flex items-center justify-center">
                <img
                src="/wezen-logo.png"
                alt="WeZen Logo"
                className="w-8 h-8 rounded-lg object-cover relative"
              />
                </div>
                <span className="font-bold text-xl text-primary-800">WeZen</span>
              </div>
              <p className="text-gray-600 mb-4 max-w-md">
                WeZen accompagne les entreprises dans l'amélioration du bien-être de leurs équipes 
                avec des solutions simples, efficaces et personnalisées.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-accent-600 transition-colors">
                  <Heart className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-violet-600 transition-colors">
                  <Users className="w-5 h-5" />
                </a>
                <a href="#" className="text-gray-400 hover:text-accent-600 transition-colors">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            {/* Solutions */}
            <div>
              <h4 className="font-semibold text-primary-800 mb-4">Solutions</h4>
              <ul className="space-y-2 text-gray-600">
                <li><a href="#" className="hover:text-accent-600 transition-colors">Méditations guidées</a></li>
                <li><a href="#" className="hover:text-violet-600 transition-colors">Pauses bien-être</a></li>
                <li><a href="#" className="hover:text-accent-600 transition-colors">Routines personnalisées</a></li>
                <li><a href="#" className="hover:text-violet-600 transition-colors">Conseils holistiques</a></li>
              </ul>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="font-semibold text-primary-800 mb-4">Contact</h4>
              <div className="space-y-2 text-gray-600">
                <a href="mailto:contact@wezen.app" className="flex items-center gap-2 hover:text-accent-600 transition-colors">
                  <Mail className="w-4 h-4" />
                  contact@wezen.app
                </a>
                <p className="text-sm">Demandez un devis personnalisé</p>
              </div>
            </div>
          </div>
          
          {/* <div className="border-t border-gray-100 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              &copy; 2024 WeZen. Tous droits réservés.
            </p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-500 hover:text-accent-600 text-sm transition-colors">
                Mentions légales
              </a>
              <a href="#" className="text-gray-500 hover:text-violet-600 text-sm transition-colors">
                Politique de confidentialité
              </a>
              <a href="#" className="text-gray-500 hover:text-accent-600 text-sm transition-colors">
                CGU
              </a>
            </div>
          </div> */}
        </div>
      </footer>
    </div>
  );
}
